import React, {useContext} from 'react'
import useTranslate from 'modules/local/useTranslate'
import {PiChalkboardSimpleBold} from 'react-icons/pi'
import {RiSubscript} from 'react-icons/ri'
import {FaChalkboard, FaPeopleRoof, FaUserGraduate, FaUserShield, FaUserTie} from 'react-icons/fa6'
import {ClassroomSvg} from '../../../../components/Svgs/ClassroomSvg'
import WorkspaceContext from '../../WorkspaceContext'
import {AccessPortalIntroBlock} from '../../AccessPortal/AccessPortalIntroBlock'

// Trang "Giới thiệu" phân hệ Đào tạo - đứng chung group với "Tổng quan & Báo cáo"
// (overview-and-intro-group, xem Workspace.js COURSE_SUB_ITEMS / CoursePortalTopNav.js
// COURSE_PORTAL_TOP_ITEMS). Nội dung TOÀN CỤC (mô hình dữ liệu + vòng đời + đối tác + phân quyền
// của CẢ phân hệ) - cố tình KHÔNG nhét vào CourseDashboard.js (đã đủ dài với số liệu vận hành thật:
// stat card, tiến độ tuyển sinh, lịch, thông báo, audit log), tách trang riêng cho gọn.
//
// Icon entity/actor dùng LẠI đúng icon đã gắn cho cùng khái niệm ở nơi khác trong module (curriculum/
// subject: menuItems WorkspaceCourse.js; course/class, student/teacher: colorMap CourseDashboard.js)
// - không tự chế bộ icon song song.
//
// Responsive: khối 4-thực-thể chuyển hàng ngang (md+) <-> cột dọc (mobile, mũi tên tự xoay 90°) bằng
// class Tailwind thuần (flex-col md:flex-row + transform rotate-90 md:rotate-0), không cần JS đo màn
// hình. Ma trận phân quyền hiện 2 bản DOM riêng: bảng thật (hidden md:block) và danh sách thẻ theo
// từng chức năng (md:hidden) - bảng 5 cột trên màn hẹp sẽ vỡ dòng rất khó đọc (đã thấy ở
// AuditTableRow/CourseDashboard.js nên áp dụng lại đúng hướng xử lý đó), thẻ dọc đọc tốt hơn nhiều.

const ENTITIES = [
    {key: 'curriculum', tag: 'curriculum', icon: PiChalkboardSimpleBold},
    {key: 'subject', tag: 'subject', icon: RiSubscript},
    {key: 'course', tag: 'course', icon: FaChalkboard},
    {key: 'class', tag: 'course_class', icon: ClassroomSvg},
]

const FLOW_CAPTION_KEYS = [
    'course intro flow caption 1',
    'course intro flow caption 2',
    'course intro flow caption 3',
]

const STAGES = [
    {n: 1, c: '#1c4a75', bg: '#e8f0f8'},
    {n: 2, c: '#2c5f8f', bg: '#eaf1f7'},
    {n: 3, c: '#ea580c', bg: '#fff7ed'},
    {n: 4, c: '#059669', bg: '#ecfdf5'},
    {n: 5, c: '#3b82f6', bg: '#eff6ff'},
    {n: 6, c: '#b45309', bg: '#fffbeb'},
]

const ACTORS = [
    {key: 'student', c: '#059669', bg: '#ecfdf5', icon: FaUserGraduate},
    {key: 'parent', c: '#7c3aed', bg: '#f3f0ff', icon: FaPeopleRoof},
    {key: 'teacher', c: '#3b82f6', bg: '#eff6ff', icon: FaUserTie},
    {key: 'admin', c: '#1c4a75', bg: '#e8f0f8', icon: FaUserShield},
]

const MATRIX_FUNCS = ['attendance', 'notification', 'grade', 'certificate', 'profile']

const SECTIONS = [
    {id: 'edu-intro-entities', labelKey: 'course intro entities title'},
    {id: 'edu-intro-lifecycle', labelKey: 'course intro lifecycle title'},
    {id: 'edu-intro-actors', labelKey: 'course intro actors title'},
    {id: 'edu-intro-matrix', labelKey: 'course intro matrix title'},
]

const SectionHeader = ({titleKey, descKey}) => {
    const t = useTranslate()
    return (
        <div className="flex flex-col gap-1">
            <h3 className="font-bold text-lg text-gray-800">{t(titleKey)}</h3>
            <p className="text-sm text-color-300 max-w-2xl">{t(descKey)}</p>
        </div>
    )
}

const CourseIntroduction = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)

    return (
        <div className="flex flex-col gap-8 pb-6">

            {/* Header - cùng pattern tiêu đề với CourseDashboard.js (title + gạch chân + subtitle) */}
            <div className="flex flex-col gap-1">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('course intro title')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <div className="mt-1 text-sm text-color-300 tracking-wider">
                    {t('course intro subtitle')}
                </div>
            </div>

            {/* Chia sẻ quyền quản trị Khóa học qua access-portal - chuyển từ CourseDashboard.js (tab
                "Tổng quan & Báo cáo") sang đây, để tab đó chỉ còn tập trung vào số liệu vận hành; đây
                là thiết lập quản trị mang tính giới thiệu/cấu hình 1 lần, hợp ngữ cảnh tab "Giới
                thiệu" hơn. Cùng vị trí tương ứng với AccessPortalIntroBlock ở FamilyBookOverviewPage.js.
                Không truyền colorPrefix="education" (giữ mặc định "amber") vì scope CSS
                --color-education-* chỉ có hiệu lực trong .education-theme (trang /edu), KHÔNG áp
                dụng ở Workspace. */}
            {/* organization?.edit = isAdmin() THẬT (cùng field ở cả OrganizationTransformer đầy đủ
                lẫn OrganizationTransformerBasic - xem checkAccess()) - CHỈ admin thật mới cấp/thu
                hồi được quyền tạm thời (FeatureAccessGrantController), nên block "chia sẻ quyền" này
                cũng chỉ nên hiện cho admin thật. Ở /workspace luôn true (chỉ admin vào được), ở
                access-portal có thể false (người chỉ có grant tạm thời xem trang này qua
                access-portal) - đây là khác biệt DUY NHẤT do giới hạn quyền, còn lại reuse y hệt. */}
            {organization?.edit && (
                <AccessPortalIntroBlock
                    organization={organization}
                    featureKey="organization_manage_course"
                    titleKey="course access portal intro title"
                    hintKey="course access portal intro hint"
                />
            )}

            {/* Nav nhảy nhanh tới từng mục - cuộn ngang trên mobile thay vì vỡ dòng */}
            <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                {SECTIONS.map((s) => (
                    <a
                        key={s.id}
                        href={`#${s.id}`}
                        className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold background-100 text-color-300 hover:background-200 no-underline whitespace-nowrap transition-colors duration-200">
                        {t(s.labelKey)}
                    </a>
                ))}
            </div>

            {/* Mô hình dữ liệu */}
            <section id="edu-intro-entities" className="flex flex-col gap-4">
                <SectionHeader titleKey="course intro entities title" descKey="course intro entities desc"/>

                <div className="flex flex-col md:flex-row md:items-stretch gap-2">
                    {ENTITIES.map((e, i) => {
                        const Icon = e.icon
                        return (
                            <React.Fragment key={e.key}>
                                <div className="flex-1 background border border-color-50 rounded-2xl shadow-sm p-4 flex flex-col gap-2 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-mono text-2xs font-bold px-2 py-0.5 rounded background-100 text-color-300 truncate">
                                            {e.tag}
                                        </span>
                                        <Icon size={18} style={{color: '#1c4a75'}} className="shrink-0"/>
                                    </div>
                                    <h4 className="font-bold text-color-000">
                                        {t(`course intro entity ${e.key} title`)}
                                    </h4>
                                    <p className="text-sm text-color-300 leading-snug">
                                        {t(`course intro entity ${e.key} desc`)}
                                    </p>
                                </div>

                                {i < ENTITIES.length - 1 && (
                                    <div className="flex md:flex-col items-center justify-center gap-1.5 md:w-16 shrink-0 py-1 md:py-0">
                                        <span
                                            style={{color: '#1c4a75'}}
                                            className="text-lg leading-none transform rotate-90 md:rotate-0 shrink-0">
                                            →
                                        </span>
                                        <span className="text-2xs font-semibold text-color-300 text-center leading-tight">
                                            {t(FLOW_CAPTION_KEYS[i])}
                                        </span>
                                    </div>
                                )}
                            </React.Fragment>
                        )
                    })}
                </div>

                <div className="text-xs text-color-300 leading-relaxed background-100 border border-dashed border-color-100 rounded-xl px-3 py-2.5">
                    {t('course intro flow note')}
                </div>
            </section>

            {/* Vòng đời vận hành */}
            <section id="edu-intro-lifecycle" className="flex flex-col gap-4">
                <SectionHeader titleKey="course intro lifecycle title" descKey="course intro lifecycle desc"/>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {STAGES.map((s) => (
                        <div
                            key={s.n}
                            style={{borderTopColor: s.c}}
                            className="background border border-color-50 border-t-4 rounded-2xl shadow-sm p-4 flex flex-col gap-2">
                            <div className="flex items-center gap-2.5">
                                <span
                                    style={{background: s.bg, color: s.c}}
                                    className="w-8 h-8 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0">
                                    {s.n}
                                </span>
                                <h4 className="font-bold text-color-000 text-sm leading-tight">
                                    {t(`course intro stage${s.n} title`)}
                                </h4>
                            </div>
                            <span style={{color: s.c}} className="text-2xs font-bold uppercase tracking-wide">
                                {t(`course intro stage${s.n} actor`)}
                            </span>
                            <p className="text-sm text-color-300 leading-snug">
                                {t(`course intro stage${s.n} desc`)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Đối tác tham gia */}
            <section id="edu-intro-actors" className="flex flex-col gap-4">
                <SectionHeader titleKey="course intro actors title" descKey="course intro actors desc"/>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {ACTORS.map((a) => {
                        const Icon = a.icon
                        return (
                            <div key={a.key} className="background border border-color-50 rounded-2xl shadow-sm p-4 flex flex-col gap-3">
                                <span
                                    style={{background: a.bg, color: a.c}}
                                    className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0">
                                    <Icon size={20}/>
                                </span>
                                <div className="flex flex-col gap-0.5">
                                    <h4 className="font-bold text-color-000">
                                        {t(`course intro actor ${a.key} title`)}
                                    </h4>
                                    <span style={{color: a.c}} className="text-2xs font-bold uppercase tracking-wide">
                                        {t(`course intro actor ${a.key} role`)}
                                    </span>
                                </div>
                                <ul className="flex flex-col gap-1.5">
                                    {[1, 2, 3].map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-sm text-color-300 leading-snug">
                                            <span style={{color: a.c}} className="shrink-0 font-bold">—</span>
                                            {t(`course intro actor ${a.key} bullet${b}`)}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )
                    })}
                </div>
            </section>

            {/* Phân quyền theo chức năng */}
            <section id="edu-intro-matrix" className="flex flex-col gap-4">
                <SectionHeader titleKey="course intro matrix title" descKey="course intro matrix desc"/>

                {/* >= md: bảng thật */}
                <div className="hidden md:block background border border-color-50 rounded-2xl shadow-sm overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="background-100 border-b border-color-50">
                                <th className="text-left px-4 py-3 text-2xs font-bold uppercase tracking-wide text-color-300 whitespace-nowrap">
                                    {t('course intro matrix function column')}
                                </th>
                                {ACTORS.map((a) => (
                                    <th
                                        key={a.key}
                                        className="text-left px-4 py-3 text-2xs font-bold uppercase tracking-wide text-color-300 whitespace-nowrap">
                                        {t(`course intro actor ${a.key} title`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {MATRIX_FUNCS.map((f, idx) => (
                                <tr key={f} className={idx < MATRIX_FUNCS.length - 1 ? 'border-b border-color-50' : ''}>
                                    <td className="px-4 py-3 font-bold text-color-000 whitespace-nowrap align-top">
                                        {t(`course intro func ${f}`)}
                                    </td>
                                    {ACTORS.map((a) => (
                                        <td key={a.key} className="px-4 py-3 text-color-300 align-top">
                                            {t(`course intro matrix ${f} ${a.key}`)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* < md: gộp theo chức năng, mỗi thẻ liệt kê 4 vai trò - bảng ngang 5 cột vỡ dòng rất
                    khó đọc trên màn hẹp (cùng vấn đề đã gặp ở AuditTableRow, CourseDashboard.js). */}
                <div className="md:hidden flex flex-col gap-3">
                    {MATRIX_FUNCS.map((f) => (
                        <div key={f} className="background border border-color-50 rounded-2xl shadow-sm p-4 flex flex-col gap-1">
                            <h4 className="font-bold text-color-000 text-sm mb-1">
                                {t(`course intro func ${f}`)}
                            </h4>
                            <div className="flex flex-col divide-y divide-slate-50">
                                {ACTORS.map((a) => (
                                    <div key={a.key} className="py-2 first:pt-0 last:pb-0 flex items-start justify-between gap-3">
                                        <span style={{color: a.c}} className="text-xs font-bold shrink-0 pt-0.5">
                                            {t(`course intro actor ${a.key} title`)}
                                        </span>
                                        <span className="text-sm text-color-300 text-right leading-snug">
                                            {t(`course intro matrix ${f} ${a.key}`)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    )
}

export default CourseIntroduction
